import React, { useState } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './auth';
import { WelcomeScreen, LoginScreen, RegisterScreen } from './AuthScreens';
import {
  HomeScreen,
  CategoriesScreen,
  MessagesScreen,
  ProfileScreen,
  BottomNavigation,
  MobileHeader,
  ChatScreen
} from './components';
import { ListingDetailScreen } from './components/ListingDetail';

// Composant pour les écrans restreints aux invités
const GuestRestrictedScreen = ({ onLogin, screenName }) => {
  const getScreenInfo = (screen) => {
    const screens = {
      messages: {
        icon: '💬',
        title: 'Messages',
        description: 'Accédez à vos conversations avec les vendeurs et acheteurs.',
        features: ['Discuter avec les vendeurs', 'Négocier les prix', 'Organiser les rencontres']
      },
      profile: {
        icon: '👤',
        title: 'Profil',
        description: 'Gérez votre profil et vos annonces.',
        features: ['Créer des annonces', 'Gérer vos favoris', 'Voir vos statistiques']
      }
    };
    return screens[screen] || screens.messages;
  };

  const info = getScreenInfo(screenName);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="text-6xl mb-4">{info.icon}</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{info.title}</h2>
        <p className="text-gray-600 mb-6">{info.description}</p>
        
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Avec un compte, vous pouvez :</h3>
          <ul className="text-sm text-blue-800 text-left space-y-1">
            {info.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={onLogin}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          Se connecter / S'inscrire
        </button>
        
        <p className="text-xs text-gray-500 mt-4">
          Déjà membre ? La connexion ne prend que quelques secondes !
        </p>
      </div>
    </div>
  );
};

// Main App Component with Authentication
function AppContent() {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('home');
  const [currentView, setCurrentView] = useState('main');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [authView, setAuthView] = useState('welcome'); // welcome, login, register
  const [isGuestMode, setIsGuestMode] = useState(false); // Mode invité

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-600 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl font-semibold">Chargement de LaTeranga...</p>
        </div>
      </div>
    );
  }

  // Fonction pour activer le mode invité
  const enableGuestMode = () => {
    setIsGuestMode(true);
    setActiveTab('home');
    setCurrentView('main');
  };

  // Fonction pour désactiver le mode invité et aller à l'auth
  const requireAuth = (targetView = 'login') => {
    setIsGuestMode(false);
    setAuthView(targetView);
  };

  // Show authentication screens if user is not logged in AND not in guest mode
  if (!user && !isGuestMode) {
    switch (authView) {
      case 'login':
        return (
          <LoginScreen 
            onSwitchToRegister={() => setAuthView('register')}
            onBack={() => setAuthView('welcome')}
          />
        );
      case 'register':
        return (
          <RegisterScreen 
            onSwitchToLogin={() => setAuthView('login')}
            onBack={() => setAuthView('welcome')}
          />
        );
      default:
        return (
          <WelcomeScreen 
            onLogin={() => setAuthView('login')}
            onRegister={() => setAuthView('register')}
            onContinueAsGuest={enableGuestMode}
          />
        );
    }
  }

  // Fonction pour gérer les actions nécessitant une authentification
  const handleAuthRequired = (action, ...args) => {
    if (!user) {
      // Afficher un modal ou rediriger vers l'authentification
      if (window.confirm('Vous devez être connecté pour effectuer cette action. Souhaitez-vous vous connecter ?')) {
        requireAuth('login');
      }
      return;
    }
    // Exécuter l'action si l'utilisateur est connecté
    action(...args);
  };

  const handleTabChange = (tab) => {
    // Vérifier si l'onglet nécessite une authentification
    const authRequiredTabs = ['messages', 'profile'];
    
    if (authRequiredTabs.includes(tab) && !user) {
      handleAuthRequired(() => {
        setActiveTab(tab);
        setCurrentView('main');
        setSelectedConversation(null);
        setSelectedListingId(null);
      });
      return;
    }

    setActiveTab(tab);
    setCurrentView('main');
    setSelectedConversation(null);
    setSelectedListingId(null);
  };

  const handleCategoryClick = (category) => {
    console.log('Category clicked:', category.name);
    // Here you would navigate to category listings
  };

  const handleListingContact = (conversation) => {
    if (!user) {
      handleAuthRequired(() => {
        console.log('🎯 handleListingContact appelé avec:', conversation);
        setSelectedConversation(conversation);
        setCurrentView('chat');
        setActiveTab('messages');
      });
      return;
    }

    console.log('🎯 handleListingContact appelé avec:', conversation);
    console.log('🎯 Type de conversation reçue:', typeof conversation);
    console.log('🎯 Structure détaillée:', JSON.stringify(conversation, null, 2));
    
    if (!conversation) {
      console.error('❌ Aucune donnée de conversation reçue');
      alert('Erreur: aucune donnée de conversation reçue');
      return;
    }
    
    // Naviguer vers la conversation nouvellement créée
    console.log('🔄 Définition de selectedConversation...');
    setSelectedConversation(conversation);
    
    console.log('🔄 Basculement vers la vue chat...');
    setCurrentView('chat');
    
    console.log('🔄 Basculement vers l\'onglet messages...');
    setActiveTab('messages');
    
    console.log('✅ Navigation vers la conversation terminée');
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    setCurrentView('chat');
  };

  const handleChatBack = () => {
    setCurrentView('main');
    setSelectedConversation(null);
    setActiveTab('messages');
  };

  const handleViewDetails = (listingId) => {
    setSelectedListingId(listingId);
    setCurrentView('listing-detail');
  };

  const handleListingDetailBack = () => {
    setCurrentView('main');
    setSelectedListingId(null);
  };

  const handleSendMessage = (message) => {
    console.log('Message sent:', message);
    // Le message est déjà envoyé dans ChatScreen
    // Ici on pourrait ajouter des actions supplémentaires si nécessaire
  };

  const handleLogout = () => {
    logout();
    setActiveTab('home');
    setCurrentView('main');
    setSelectedConversation(null);
    setSelectedListingId(null);
    setAuthView('welcome');
  };

  const handleCreateAd = () => {
    console.log('Create ad clicked from profile');
    // Basculer vers l'onglet home qui gère la création d'annonces
    setActiveTab('home');
    // Ici on pourrait ajouter un état pour ouvrir directement le formulaire
  };

  const renderCurrentScreen = () => {
    if (currentView === 'chat') {
      return (
        <ChatScreen 
          conversation={selectedConversation}
          onBack={handleChatBack}
          onSend={handleSendMessage}
        />
      );
    }

    if (currentView === 'listing-detail') {
      return (
        <ListingDetailScreen 
          listingId={selectedListingId}
          onBack={handleListingDetailBack}
          onContact={handleListingContact}
        />
      );
    }

    switch (activeTab) {
      case 'home':
        return (
          <HomeScreen 
            onCategoryClick={handleCategoryClick}
            onListingContact={handleListingContact}
            onViewDetails={handleViewDetails}
            isGuest={!user}
            onRequireAuth={() => requireAuth('login')}
          />
        );
      case 'categories':
        return <CategoriesScreen onCategoryClick={handleCategoryClick} onViewDetails={handleViewDetails} />;
      case 'messages':
        if (!user) {
          return <GuestRestrictedScreen onLogin={() => requireAuth('login')} screenName="messages" />;
        }
        return <MessagesScreen onConversationClick={handleConversationClick} />;
      case 'profile':
        if (!user) {
          return <GuestRestrictedScreen onLogin={() => requireAuth('login')} screenName="profile" />;
        }
        return (
          <ProfileScreen 
            onLogout={handleLogout} 
            onCreateAd={handleCreateAd}
          />
        );
      default:
        return (
          <HomeScreen 
            onCategoryClick={handleCategoryClick}
            onListingContact={handleListingContact}
            onViewDetails={handleViewDetails}
            isGuest={!user}
            onRequireAuth={() => requireAuth('login')}
          />
        );
    }
  };

  return (
    <div className="App min-h-screen bg-gray-50">
      {/* Mobile Header for non-chat and non-detail views */}
      {currentView !== 'chat' && currentView !== 'listing-detail' && (
        <MobileHeader 
          title="LaTeranga"
          rightAction={
            <button 
              onClick={handleLogout}
              className="p-1 text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          }
        />
      )}

      {/* Main Content */}
      {renderCurrentScreen()}

      {/* Bottom Navigation - Hide during chat and detail views */}
      {currentView !== 'chat' && currentView !== 'listing-detail' && (
        <BottomNavigation 
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
